import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Generation, GenerationStatus, GenerationType } from '../types/generation';

interface GenerationsResponse {
  items: Generation[];
  nextCursor: string | null;
}

interface GenerationsFilters {
  type?: GenerationType;
  status?: GenerationStatus;
  productId?: string;
  from?: string;
  to?: string;
  limit?: number;
}

function toQuery(params: GenerationsFilters & { cursor?: string }): string {
  const q = new URLSearchParams();
  if (params.cursor) q.set('cursor', params.cursor);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.type) q.set('type', params.type);
  if (params.status) q.set('status', params.status);
  if (params.productId) q.set('productId', params.productId);
  if (params.from) q.set('from', params.from);
  if (params.to) q.set('to', params.to);
  const str = q.toString();
  return str ? `?${str}` : '';
}

function hasActive(items: Generation[]): boolean {
  return items.some(
    (g) => g.status === 'pending' || g.status === 'preprocessing' || g.status === 'processing',
  );
}

export function useGenerations(filters: GenerationsFilters = {}) {
  return useInfiniteQuery({
    queryKey: ['generations', filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api<GenerationsResponse>(
        `/generations${toQuery({ ...filters, limit: filters.limit ?? 20, cursor: pageParam })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages ?? [];
      const all = pages.flatMap((p) => p.items);
      return hasActive(all) ? 5000 : false;
    },
  });
}

export function useGeneration(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['generation', id],
    queryFn: () => api<Generation>(`/generations/${id}`),
    enabled: enabled && !!id,
    refetchInterval: (query) => {
      const st = query.state.data?.status;
      if (!st) return false;
      return st === 'completed' || st === 'failed' || st === 'cancelled' ? false : 2000;
    },
  });
}
